import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, Users, X, ArrowRight } from 'lucide-react';
import { usePlannerReminders, planRemKey } from './usePlannerReminders';

// Friendly "when" line for a due reminder, built from the item's local date +
// start time (times are stored HH:MM in the user's local zone).
function whenLabel(n) {
  if (n.all_day) return 'All day';
  if (!n.date) return '';
  const iso = n.start_time ? `${n.date}T${n.start_time}:00` : `${n.date}T09:00:00`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return n.start_time || '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today · ${time}`;
  return `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} · ${time}`;
}

/**
 * Floating in-app reminder alerts for Planner events + meetings. Drop once into
 * any Notes-suite shell; it polls on its own and renders a bottom-right stack of
 * due reminders with a chime + OS notification. Theme-agnostic dark toast so it
 * reads correctly on both the Notes (--n-*) and Planner (--p-*) surfaces.
 */
export function PlannerReminderAlerts() {
  const { dueQueue, acknowledge } = usePlannerReminders();
  const navigate = useNavigate();
  if (!dueQueue.length) return null;

  const open = (n) => {
    acknowledge(n);
    navigate(`/notes/planner?date=${encodeURIComponent(n.date || '')}&view=daily`);
  };

  return (
    <div
      className="fixed z-[300] bottom-4 right-4 flex flex-col gap-2 w-[330px] max-w-[calc(100vw-2rem)]"
      data-testid="planner-reminder-alerts"
      role="region"
      aria-label="Planner reminders"
    >
      {dueQueue.slice(0, 4).map((n) => {
        const isMeeting = n.kind === 'meeting';
        return (
          <div
            key={planRemKey(n)}
            className="rounded-xl border border-white/10 bg-slate-900/95 text-white shadow-xl backdrop-blur px-3.5 py-3 flex items-start gap-3 animate-in slide-in-from-bottom-2 fade-in duration-200"
            data-testid={`planner-reminder-${n.kind}-${n.id}`}
          >
            <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isMeeting ? 'bg-amber-500/20 text-amber-300' : 'bg-indigo-500/20 text-indigo-300'}`}>
              {isMeeting ? <Users size={15} /> : <CalendarClock size={15} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wide text-white/50">
                {isMeeting ? 'Meeting reminder' : 'Event reminder'}
              </div>
              <div className="font-medium text-sm truncate" data-testid={`planner-reminder-title-${n.id}`}>
                {n.title}
              </div>
              <div className="text-[11px] text-white/60">{whenLabel(n)}</div>
              <button
                type="button"
                onClick={() => open(n)}
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-amber-300 hover:text-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 rounded"
                data-testid={`planner-reminder-open-${n.id}`}
              >
                Open in planner <ArrowRight size={11} />
              </button>
            </div>
            <button
              type="button"
              onClick={() => acknowledge(n)}
              className="text-white/40 hover:text-white/80 transition-colors flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded"
              aria-label="Dismiss reminder"
              data-testid={`planner-reminder-dismiss-${n.id}`}
            >
              <X size={15} />
            </button>
          </div>
        );
      })}
      {dueQueue.length > 4 && (
        <div className="rounded-lg border border-white/10 bg-slate-900/90 text-white/70 text-[11px] text-center px-3 py-1.5" data-testid="planner-reminder-more">
          +{dueQueue.length - 4} more reminder{dueQueue.length - 4 === 1 ? '' : 's'} due
        </div>
      )}
    </div>
  );
}

export default PlannerReminderAlerts;
